/**
 * ProjectRow — list-view row matching designs/Projects.html .lrow layout.
 *
 * Columns: Project (color dot + name) / Status / Progress / Team / Due
 */

import { Link } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";
import { Pill } from "@/components/Pill";
import { ProgressBar } from "@/components/ProgressBar";
import { AvatarGroup } from "@/components/AvatarGroup";
import { TagChip } from "@/components/TagChip";
import { formatDateShort } from "@/lib/format";
import { PROJECT_STATUS_LABEL, projectPillVariant } from "@/lib/projectStatus";
import type { ProjectCardProject } from "@/components/ProjectCard";

function formatDue(due: string | number | null | undefined): string | null {
  const s = formatDateShort(due);
  return s || null;
}

export function ProjectRow({ project: p }: { project: ProjectCardProject }) {
  const total = p.totalCount ?? 0;
  const completed = p.completedCount ?? 0;
  const dueDateLabel = formatDue(p.targetDate);
  const members = (p.members ?? []).map((m) => ({
    ...m,
    name: m.name ?? m.id,
  }));

  return (
    <Link
      to="/projects/$projectId"
      params={{ projectId: p.id }}
      className="lrow"
    >
      <div className="ln">
        <span
          className="pc-ic"
          style={{ background: p.color ?? "var(--green-500)" }}
        >
          {p.icon ? <Icon name={p.icon} size={15} /> : null}
        </span>
        <div>
          <div className="lname">{p.name}</div>
          {p.category ? <div className="pc-cat">{p.category}</div> : null}
          {(p.tags ?? []).length > 0 ? (
            <div className="pc-tags">
              {(p.tags ?? []).slice(0, 3).map((t) => (
                <TagChip key={t.id} name={t.name} color={t.color} />
              ))}
              {(p.tags ?? []).length > 3 ? (
                <span className="t-xs t-muted">
                  +{(p.tags ?? []).length - 3}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <Pill variant={projectPillVariant(p.status)} dot>
          {PROJECT_STATUS_LABEL[p.status] ?? p.status}
        </Pill>
      </div>
      <div className="lprog">
        {total > 0 ? (
          <ProgressBar value={completed} total={total} label="count" />
        ) : (
          <span className="t-muted">—</span>
        )}
      </div>
      <div>
        {members.length > 0 ? (
          <AvatarGroup users={members} max={3} size={18} />
        ) : (
          <span className="t-muted">—</span>
        )}
      </div>
      <div className="t-mono t-muted">{dueDateLabel ?? "—"}</div>
    </Link>
  );
}
